import React from 'react';
import { Scrim } from '@att-bit/duc.fragments.scrim';
import { ConditionalWrapper } from '@att-bit/duc.utils.conditional-wrapper';

export type SpinnerProps = {
  /** children (text/jsx) to be rendered in component */
  children?: string,
  /** should be used to pass the extra classes */
  className?: string,
  /** Determines whether this spinner is rendered on a dark background. This only changes the color of the optional label. 
   * Defaults to `false`. */
  onDark?: boolean;
  /** color for the spinnerType = container, default  is scrimDark. ScrimLight is another option*/
  scrimColor?: ScrimShade,
  /** Determines type of spinner. (button, container,  fullpage) */
  variant?: SpinnerVariant,
  /** Determines size of spinner (height-xl-all width-xl-all, height-lg-all width-lg-all, height-md-all width-md-all, 
   * height-sm-all width-sm-all) Size to be sent as  xlarge, large, medium, small */
  size?: SpinnerSize,
  /** styles to be passed */
  style?: object,
  /** for fullpage: use true if the spinner is in a custom scrim, false spinner will provide its own scrim */
  inScrim?: boolean, 
  /** used to change the role depending on the application status. Defaults to `status` */ 
  role?:string 

};

export enum ScrimShade {
  scrimLight = "bg-rgba-white-85",
  scrimDark = "bg-rgba-black-85"
}

export enum SpinnerVariant {
  BUTTON = "button",
  CONTAINER = "container",
  FULLPAGE = "fullpage"
}

export enum SpinnerSize {
  xlarge = "height-xl-all width-xl-all",
  large = "height-lg-all width-lg-all",
  medium = "height-md-all width-md-all",
  small = "height-sm-all width-sm-all",
}

export function Spinner({
  children,
  size = SpinnerSize.large,
  onDark = false,
  inScrim = false,
  scrimColor = ScrimShade.scrimDark,
  variant = SpinnerVariant.CONTAINER,
  role='status',

  ...props
}: SpinnerProps) {

  const svg = (
    <svg viewBox="0 0 43 43"  aria-hidden={props['aria-hidden']}  aria-label={props['aria-label']}  className={`duc-loading-spinner ${props['className']} `} style={props['style']}>
      <g>
        <circle className="duc-loading-spinner-inner" r="20" cx="21.5" cy="21.5" fill="none"></circle>
        <circle className="duc-loading-spinner-outer" r="20" cx="21.5" cy="21.5" fill="none"></circle>
      </g>
    </svg>
  );

  // RENDER ##############################################################
  const spinnerDiv = (
    <div className={`text-center`}>
      <div
        className={`inline-block ${size}`}
        data-testid={props['data-testid']}
        role={role}
        aria-busy="true"
        aria-label={props['aria-label']}
      >
        {svg}
      </div>
      {children && (
        <p className={`type-sm mar-t-xxs pad-l-lg pad-r-lg ${onDark || variant === SpinnerVariant.FULLPAGE ? "color-gray-300" : "color-gray-700"}`}>{children}</p>
      )}
  </div>
  );

  const fullPageWrap = (jsx) => {
    return (
      <div className="fixed top left height-full width-full flex flex-column justify-center z3">{jsx}</div>
    );
  }

  return (
    <>
      {variant === SpinnerVariant.BUTTON ? (
               <svg viewBox="0 0 43 43" aria-hidden={props['aria-hidden']} className={`duc-loading-spinner ${props['className']} `} aria-label={props['aria-label']} style={props['style']}>
               <g>
                 <circle className="duc-loading-spinner-inner" r="20" cx="21.5" cy="21.5" fill="none"></circle>
                 <circle className="duc-loading-spinner-outer" r="20" cx="21.5" cy="21.5" fill="none"></circle>
               </g>
             </svg>
      ) : (variant === SpinnerVariant.FULLPAGE) ? (
        <>
          <ConditionalWrapper
            condition={inScrim === false}
            wrapper={children => <Scrim show={true}>{children}</Scrim>}
          >
              {fullPageWrap(spinnerDiv)}
          </ConditionalWrapper>
        </>
      ) : (variant === SpinnerVariant.CONTAINER) ? (
        <div id="spinner" className={`absolute-fill flex flex-items-center justify-center radius-xs ${scrimColor}`}>
          {spinnerDiv}
        </div>
      ) : (
        <div>
          {spinnerDiv}
        </div>
      )
      }

      <style jsx global>{`
        @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
          .duc-loading-spinner {
              animation: duc-loading-spinner 2.25s linear infinite;
              transform-origin: center center;
          }
        }
        .duc-loading-spinner g {
          animation: duc-loading-spinner 2.25s linear infinite;
          transform-origin: center center;
        }
        .duc-loading-spinner-inner {
          stroke-width: 3;
          stroke: #878c94;
          opacity: 0.25;
        }
        .duc-loading-spinner-outer {
          stroke-width: 3;
          stroke-dasharray: 89, 200;
          stroke-dashoffset: 0;
          stroke: #009fdb;
          animation: duc-loading-spinner-outer 1.6875s ease-in-out infinite;
          stroke-linecap: round;
        }
        @keyframes duc-loading-spinner {
          100% {
              transform: rotate(360deg);
          }
        }
        @keyframes duc-loading-spinner-outer {
          0% {
              stroke-dasharray: 1, 200;
              stroke-dashoffset: 0;
          }
          50% {
              stroke-dasharray: 89, 200;
              stroke-dashoffset: -35px;
          }
          100% {
              stroke-dasharray: 89, 200;
              stroke-dashoffset: -124px;
          }
        }
      `}</style>
    </>
  );
}

// Spinner.allowInScrim = true;